/**
 * Transit info / editor. A transit is two charted objects in line — a leading
 * line you follow, keyed to the two objects' charted positions, so its bearing
 * is fixed by the marks (not editable here). Name it, file it under a pilotage
 * plan, or delete it. Read-only in Live, editable in Plotting, the same as
 * clearing bearings and waypoints.
 */
import { useEffect, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { usePilotageStore } from '@/store/pilotage';
import { transitBearingDeg, transitDisplayName } from '@/pilotage';
import { formatBearing, formatPosition } from '@/nav';
import { Sheet } from './Sheet';

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="acp-kv">
      <span className="k">{k}</span>
      <span className="v">{v}</span>
    </div>
  );
}

export function TransitSheet() {
  const activeId = usePilotageStore((s) => s.activeTransitId);
  const transits = usePilotageStore((s) => s.transits);
  const plans = usePilotageStore((s) => s.plans);
  const updateTransit = usePilotageStore((s) => s.updateTransit);
  const removeTransit = usePilotageStore((s) => s.removeTransit);
  const setActiveTransit = usePilotageStore((s) => s.setActiveTransit);
  const assignTransitToPlan = usePilotageStore((s) => s.assignTransitToPlan);
  const addPlan = usePilotageStore((s) => s.addPlan);

  const mode = useShellStore((s) => s.mode);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);

  const t = transits.find((x) => x.id === activeId);

  const [name, setName] = useState('');
  useEffect(() => {
    if (t) setName(t.name ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!t) return <Sheet name="transit" title="Transit" />;

  const brg = transitBearingDeg(t);
  // The line reads both ways; show the bearing and its reciprocal so it can be
  // checked whichever way it's approached.
  const bearingText = `${formatBearing(brg)}  (or ${formatBearing((brg + 180) % 360)})`;
  const readOnly = mode === 'Live';

  if (readOnly) {
    return (
      <Sheet
        name="transit"
        title="Transit"
        sub="Two charted objects in line: when they line up, we're on this bearing. Swap it front-to-back or delete it here; the bearing itself comes from the two marks."
      >
        <Row k="Name" v={transitDisplayName(t)} />
        <Row k="Front object" v={t.bLabel || 'Charted object'} />
        <Row k="Rear object" v={t.aLabel || 'Charted object'} />
        <Row k="Transit bearing" v={bearingText} />
        <Row k="Object positions" v={`${formatPosition(t.a)} · ${formatPosition(t.b)}`} />
        {/* The transit bearing is fixed by the two marks, so there's no bearing
            to type here. Swapping back/front reverses which way we run it (the
            reciprocal); deleting is removal, allowed in Live like a clearing. */}
        <div className="acp-btn-row">
          <button
            type="button"
            className="acp-btn full"
            onClick={() => {
              updateTransit(t.id, { a: t.b, b: t.a, aLabel: t.bLabel, bLabel: t.aLabel });
              showToast('Swapped back and front');
            }}
          >
            Swap back / front (reverse)
          </button>
        </div>
        <div className="acp-btn-row">
          <button
            type="button"
            className="acp-btn"
            onClick={() => {
              removeTransit(t.id);
              setActiveTransit(null);
              closeSheet();
              showToast('Transit deleted');
            }}
          >
            Delete
          </button>
          <button
            type="button"
            className="acp-btn primary"
            onClick={() => {
              setActiveTransit(null);
              closeSheet();
            }}
          >
            Done
          </button>
        </div>
      </Sheet>
    );
  }

  function del() {
    if (!t) return;
    removeTransit(t.id);
    setActiveTransit(null);
    closeSheet();
    showToast('Transit deleted');
  }

  function swap() {
    if (!t) return;
    updateTransit(t.id, { a: t.b, b: t.a, aLabel: t.bLabel, bLabel: t.aLabel });
    showToast('Swapped back and front');
  }

  return (
    <Sheet
      name="transit"
      title="Transit"
      sub="A directional leading line: the back mark in line behind the front gives this bearing to run in on. The bearing comes from the two marks; drag the open end on the chart to extend or shorten the run."
    >
      <Row k="Transit bearing" v={bearingText} />
      <Row k="Back (rear) mark" v={t.aLabel || 'object / point'} />
      <Row k="Front mark" v={t.bLabel || 'object / point'} />
      <div className="acp-btn-row">
        <button type="button" className="acp-btn full" onClick={swap}>
          Swap back / front
        </button>
      </div>
      <label className="acp-fieldlabel">Name</label>
      <input
        className="acp-text-input"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          updateTransit(t.id, { name: e.target.value });
        }}
        placeholder={transitDisplayName(t)}
      />
      <label className="acp-fieldlabel">Pilotage plan</label>
      <div className="acp-seg" style={{ flexWrap: 'wrap' }}>
        <button type="button" className={t.planId === null ? 'on' : ''} onClick={() => assignTransitToPlan(t.id, null)}>
          Loose
        </button>
        {plans.map((p) => (
          <button key={p.id} type="button" className={t.planId === p.id ? 'on' : ''} onClick={() => assignTransitToPlan(t.id, p.id)}>
            {p.name}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            const id = addPlan();
            assignTransitToPlan(t.id, id);
            showToast('Added to a new plan');
          }}
        >
          + New plan
        </button>
      </div>
      <div className="acp-btn-row">
        <button type="button" className="acp-btn" onClick={del}>
          Delete
        </button>
        <button
          type="button"
          className="acp-btn primary"
          onClick={() => {
            setActiveTransit(null);
            closeSheet();
          }}
        >
          Done
        </button>
      </div>
    </Sheet>
  );
}
